import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getUserFromToken, type AuthUser } from "@/lib/auth";

const TOKEN_KEY = "fluxabee-auth-token";
const EMAIL_KEY = "fluxabee-auth-email";

interface AuthContextType {
  user: AuthUser | null;
  email: string | null;
  token: string | null;
  isLoading: boolean;
  login: (token: string, email: string) => void;
  logout: () => void;
  updateToken: (token: string) => void;
  loginDialogOpen: boolean;
  openLoginDialog: () => void;
  closeLoginDialog: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedEmail = localStorage.getItem(EMAIL_KEY);
    if (storedToken) {
      const u = getUserFromToken(storedToken);
      if (u) {
        setToken(storedToken);
        setUser(u);
        setEmail(storedEmail);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMAIL_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback((newToken: string, newEmail: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(EMAIL_KEY, newEmail);
    setToken(newToken);
    setEmail(newEmail);
    setUser(getUserFromToken(newToken));
    setLoginDialogOpen(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setToken(null);
    setUser(null);
    setEmail(null);
  }, []);

  const updateToken = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(getUserFromToken(newToken));
  }, []);

  const openLoginDialog = useCallback(() => setLoginDialogOpen(true), []);
  const closeLoginDialog = useCallback(() => setLoginDialogOpen(false), []);

  return (
    <AuthContext.Provider
      value={{ user, email, token, isLoading, login, logout, updateToken, loginDialogOpen, openLoginDialog, closeLoginDialog }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
